import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Pagination, CircularProgress } from "@mui/material";
import { MdAdd } from "react-icons/md";
import {
  fetchUserAddresses,
  setDefaultAddress,
  deleteAddress,
  setSelectedAddress,
} from "../../store/slices/AddressSlice";
import AddressCard from "../AddressCard";
import AddAddressModal from "../AddAddressModal";
import EditAddressModal from "../EditAddressModal";
import { toast } from "react-toastify";
import "./style.css";

const AddressManager = ({ onAddressSelect, selectedAddressId }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const dispatch = useDispatch();
  const {
    addresses,
    pagination,
    loading,
    error,
    setDefaultLoading,
    deleteLoading,
    selectedAddress,
  } = useSelector((state) => state.address);

  useEffect(() => {
    dispatch(fetchUserAddresses({ page: 1, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    // Auto-select default address if no address is selected
    if (!selectedAddressId && addresses.length > 0) {
      const defaultAddr = addresses.find((addr) => addr.isDefault);
      if (defaultAddr && onAddressSelect) {
        onAddressSelect(defaultAddr);
      }
    }
  }, [addresses, selectedAddressId, onAddressSelect]);

  const handlePageChange = (event, page) => {
    dispatch(fetchUserAddresses({ page, limit: pagination.limit }));
  };

  const handleSetDefault = async (addressId) => {
    try {
      await dispatch(setDefaultAddress(addressId)).unwrap();
      toast.success("Default address updated successfully!");
    } catch (error) {
      toast.error(error || "Failed to set default address");
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await dispatch(deleteAddress(addressId)).unwrap();
      toast.success("Address deleted successfully!");
    } catch (error) {
      toast.error(error || "Failed to delete address");
    }
  };

  const handleAddressSelect = (address) => {
    if (onAddressSelect) {
      onAddressSelect(address);
    }
  };

  const handleAddSuccess = () => {
    // Refresh the address list
    dispatch(fetchUserAddresses({ page: 1, limit: pagination.limit }));
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    // Refresh the address list
    dispatch(
      fetchUserAddresses({
        page: pagination.currentPage,
        limit: pagination.limit,
      })
    );
    setIsEditModalOpen(false);
    setEditingAddress(null);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditingAddress(null);
  };

  if (loading && addresses.length === 0) {
    return (
      <div className="address-manager">
        <div className="loading-container">
          <CircularProgress />
          <p>Loading addresses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="address-manager">
        <div className="error-container">
          <p>Error loading addresses: {error}</p>
          <Button
            variant="outlined"
            onClick={() => dispatch(fetchUserAddresses({ page: 1, limit: 10 }))}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="address-manager">
      <div className="address-header">
        <h3>Select Delivery Address</h3>
        <Button
          variant="contained"
          startIcon={<MdAdd />}
          onClick={() => setIsAddModalOpen(true)}
          className="add-address-btn"
        >
          Add New Address
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="no-addresses">
          <p>No addresses found. Please add a new address.</p>
          <Button
            variant="contained"
            startIcon={<MdAdd />}
            onClick={() => setIsAddModalOpen(true)}
            className="add-first-address-btn"
          >
            Add Your First Address
          </Button>
        </div>
      ) : (
        <>
          <div className="addresses-list">
            {addresses.map((address) => (
              <AddressCard
                key={address.id}
                address={address}
                isSelected={selectedAddressId === address.id}
                onSelect={() => handleAddressSelect(address)}
                onSetDefault={handleSetDefault}
                onEdit={handleEdit}
                onDelete={handleDeleteAddress}
                setDefaultLoading={setDefaultLoading}
                deleteLoading={deleteLoading}
                showActions={true}
              />
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination-container">
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </div>
          )}
        </>
      )}

      <AddAddressModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />

      <EditAddressModal
        open={isEditModalOpen}
        onClose={handleEditClose}
        address={editingAddress}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default AddressManager;
